import { makeAutoObservable, runInAction, reaction } from 'mobx'
import { initializeYmaps } from '../../../../shared/api/ymaps'
import { getAddressFromCoords } from '../../../../shared/api/ymaps/geocoder'

const MAX_CACHE_SIZE = 100 // Ограничение на количество записей в кэше

class MapStore {
  // Компоненты и API будут загружены асинхронно и помещены сюда.
  // Изначально они null, чтобы компоненты UI не пытались их рендерить.
  ymaps3 = null
  YMap = null
  YMapDefaultSchemeLayer = null
  YMapDefaultFeaturesLayer = null
  YMapControls = null
  YMapMarker = null
  YMapZoomControl = null
  YMapGeolocationControl = null
  YMapListener = null
  isLoading = true // Начинаем с состояния загрузки
  isGeocoding = false
  initPromise = null
  YMapDefaultMarker = null
  YMapSearchControl = null

  // Состояние, которое будет использовать React-компонент карты
  location = {
    center: [37.57, 55.75], // В v3 API порядок: [longitude, latitude]
    zoom: 10,
  }
  placemarkCoords = null

  // Состояния для поиска в MapPickerModal
  searchText = ''
  suggestions = []
  isSearching = false
  isDropdownOpen = false
  selectedLocation = null // Выбранное местоположение { text, geo }
  geocodingController = null

  // Кэш для результатов геокодирования
  searchCache = new Map()
  reverseGeocodeCache = new Map()
  isCacheEnabled = true // Флаг для управления кэшированием

  constructor(rootStore) {
    makeAutoObservable(this, { rootStore: false }, { autoBind: true })
    this.errorStore = rootStore.errorStore
    // `initPromise` гарантирует, что любой вызов `search` или `getAddressByCoords`
    // дождется полной загрузки API Яндекс.Карт.
    this.initPromise = this.init()

    // Реакция на изменение текста поиска с задержкой (debounce)
    reaction(
      () => this.searchText,
      () => this._fetchSuggestions(),
      { fireImmediately: false, delay: 500 },
    )
  }

  _manageCacheSize(cache) {
    if (cache.size > MAX_CACHE_SIZE) {
      // Map сохраняет порядок вставки, поэтому первый ключ - самый старый.
      const oldestKey = cache.keys().next().value
      cache.delete(oldestKey)
    }
  }

  async init() {
    try {
      // Вызываем единую функцию инициализации, которая вернет все необходимое.
      const initialYmaps = await initializeYmaps()
      // После успешной загрузки, обновляем состояние стора.
      // MobX уведомит все наблюдающие компоненты (MapPickerModal).
      runInAction(() => {
        this.ymaps3 = initialYmaps.ymaps3
        this.YMap = initialYmaps.YMap
        this.YMapDefaultSchemeLayer = initialYmaps.YMapDefaultSchemeLayer
        this.YMapDefaultFeaturesLayer = initialYmaps.YMapDefaultFeaturesLayer
        this.YMapControls = initialYmaps.YMapControls
        this.YMapMarker = initialYmaps.YMapMarker
        this.YMapZoomControl = initialYmaps.YMapZoomControl
        this.YMapGeolocationControl = initialYmaps.YMapGeolocationControl
        this.YMapDefaultMarker = initialYmaps.YMapDefaultMarker
        this.YMapSearchControl = initialYmaps.YMapSearchControl
        this.YMapListener = initialYmaps.YMapListener
      })
    } catch (e) {
      console.error('Ошибка загрузки API Яндекс.Карт:', e)
      runInAction(() => {
        this.errorStore.showError('Не удалось загрузить API карт')
      })
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  // --- Методы для управления состоянием карты ---
  setLocation(newLocation) {
    // Защита от установки некорректных координат
    if (
      newLocation.center &&
      (!Array.isArray(newLocation.center) ||
        newLocation.center.length !== 2 ||
        !isFinite(newLocation.center[0]) ||
        !isFinite(newLocation.center[1]))
    ) {
      console.error(
        '[MapStore] Attempted to set invalid center coordinates:',
        newLocation.center,
      )
      // Если координаты невалидны, не обновляем центр, чтобы избежать падения карты.
      // Можно установить значение по умолчанию или просто проигнорировать.
      // Здесь мы просто удаляем невалидное значение из объекта обновления.
      delete newLocation.center
    }
    this.location = { ...this.location, ...newLocation }
  }

  setPlacemark(coords) {
    this.placemarkCoords = coords
  }

  setSearchText(text) {
    this.searchText = text
    if (text) {
      this.isDropdownOpen = true
    }
  }

  setDropdownOpen(isOpen) {
    this.isDropdownOpen = isOpen
  }

  // --- Методы, перенесенные из компонента ---

  async initialize({ initialCenter, initialText }) {
    // Сброс состояния при каждом открытии модального окна
    this.setPlacemark(null)
    this.selectedLocation = null
    this.searchText = initialText || ''
    this.suggestions = []
    this.isDropdownOpen = false

    try {
      // 1. Если есть координаты
      if (initialCenter && initialCenter.lat && initialCenter.lng) {
        const coords = [initialCenter.lng, initialCenter.lat]
        this.setLocation({ center: coords, zoom: 15 })
        this.setPlacemark(coords)
        this.selectedLocation = {
          geo: initialCenter,
          text: initialText || '',
        }
        this.searchText = initialText || ''
        return
      }

      // 2. Если есть текст, но нет координат
      if (initialText) {
        const results = await this.search(initialText)
        if (results && results.length > 0) {
          const firstResult = results[0]
          const coords = [firstResult.geo.longitude, firstResult.geo.latitude]
          this.setPlacemark(coords)
          this.setLocation({ center: coords, zoom: 15 })
          this.selectedLocation = firstResult
          this.searchText = firstResult.text
        }
        return
      }

      // 3. Попытка определить по геолокации браузера
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords
            this.updateLocationFromCoords([longitude, latitude])
          },
          (error) => {
            console.warn(
              `Ошибка получения геолокации: ${error.message}. Используем центр по умолчанию.`,
            )
            this.setLocation({ center: [37.57, 55.75], zoom: 10 })
          },
        )
      } else {
        // 4. Если ничего нет — сбрасываем на центр по умолчанию
        this.setLocation({ center: [37.57, 55.75], zoom: 10 })
      }
    } catch (error) {
      console.error('Ошибка инициализации карты', error)
      this.errorStore.showError('Не удалось инициализировать карту')
    }
  }

  async _fetchSuggestions() {
    if (!this.searchText || this.searchText.length < 3) {
      this.suggestions = []
      return
    }

    this.isSearching = true
    try {
      const results = await this.suggest(this.searchText)
      runInAction(() => {
        this.suggestions = results || []
      })
    } catch (error) {
      console.error('Ошибка при поиске подсказок:', error)
      this.errorStore.showError('Ошибка при поиске подсказок')
      runInAction(() => (this.suggestions = []))
    } finally {
      runInAction(() => {
        this.isSearching = false
      })
    }
  }

  // Поиск адреса (геокодирование)
  async search(query) {
    // Перед выполнением поиска убедимся, что API готово.
    // Это защищает от вызовов до завершения асинхронной инициализации.
    await this.initPromise // Ждем завершения инициализации
    if (!this.ymaps3 || !query) return null // Проверяем еще раз на случай ошибки

    // Проверяем кэш перед выполнением запроса
    if (this.isCacheEnabled && this.searchCache.has(query)) {
      // LRU: При попадании в кэш, перемещаем элемент в конец, делая его "самым свежим".
      const cachedResult = this.searchCache.get(query)
      this.searchCache.delete(query)
      this.searchCache.set(query, cachedResult)
      return cachedResult
    }

    try {
      // Вместо подсказки по городу (hintCity) используем текущий центр карты.
      // API будет отдавать приоритет результатам в видимой области.
      const searchOptions = {
        text: query,
        ll: this.location.center, // [longitude, latitude]
      }
      const result = await this.ymaps3.search(searchOptions)
      if (result && result.length > 0) {
        // Преобразуем результаты в наш внутренний формат и возвращаем массив
        const mappedResult = result.map((item) => ({
          text: item.properties.name,
          geo: {
            latitude: item.geometry.coordinates[1],
            longitude: item.geometry.coordinates[0],
          },
        }))
        // Сохраняем результат в кэш
        if (this.isCacheEnabled) {
          this.searchCache.set(query, mappedResult)
          this._manageCacheSize(this.searchCache)
        }
        return mappedResult
      }
      // Если ничего не найдено, не показываем ошибку, а просто возвращаем пустой массив
      return []
    } catch (error) {
      console.error('Ошибка поиска адреса:', error)
      this.errorStore.showError('Произошла ошибка при поиске адреса.')
      return null
    }
  }

  // Получение подсказок по адресу
  async suggest(query) {
    await this.initPromise
    if (!this.ymaps3 || !query) return []

    try {
      const suggestOptions = {
        text: query,
        // Преобразуем MobX Proxy в обычный массив, который ожидает API карт.
        // Оператор spread (...) создает неглубокую копию.
        center: [...this.location.center],
        // Span должен быть массивом чисел [delta_longitude, delta_latitude]
        span: [0.1, 0.1],
        types: 'house',
        limit: 5,
      }
      // API подсказок возвращает массив объектов с полями title, subtitle, text
      const suggestions = await this.ymaps3.suggest(suggestOptions)
      return suggestions || []
    } catch (error) {
      console.error('Ошибка при получении подсказок:', error)
      this.errorStore.showError('Не удалось загрузить подсказки')
      throw error // Пробрасываем ошибку, чтобы вызывающий код мог ее обработать
    }
  }

  // Получение адреса по координатам (обратное геокодирование)
  async getAddressByCoords(coords) {
    await this.initPromise
    if (!coords) return null

    // Создаем ключ для кэша и проверяем его наличие
    const cacheKey = coords.join(',')
    if (this.isCacheEnabled && this.reverseGeocodeCache.has(cacheKey)) {
      // LRU: При попадании в кэш, перемещаем элемент в конец.
      const cachedResult = this.reverseGeocodeCache.get(cacheKey)
      this.reverseGeocodeCache.delete(cacheKey)
      this.reverseGeocodeCache.set(cacheKey, cachedResult)
      // Возвращаем данные из кэша, обернув их в Promise для единообразия
      return Promise.resolve(cachedResult)
    }

    // Отменяем предыдущий запрос, если он еще выполняется
    if (this.geocodingController) {
      this.geocodingController.abort()
    }
    this.geocodingController = new AbortController()
    const { signal } = this.geocodingController

    this.isGeocoding = true
    try {
      const address = await getAddressFromCoords(coords, { signal })
      if (address) {
        const result = {
          text: address,
          geo: { latitude: coords[1], longitude: coords[0] },
        }
        // Сохраняем результат в кэш
        if (this.isCacheEnabled) {
          this.reverseGeocodeCache.set(cacheKey, result)
          this._manageCacheSize(this.reverseGeocodeCache)
        }
        return result
      }
      return null
    } catch (error) {
      console.error('Ошибка обратного геокодирования:', error)
      // Не показываем ошибку, если запрос был отменен пользователем
      if (error.name !== 'AbortError') {
        this.errorStore.showError('Не удалось определить адрес.')
      }
      return null
    } finally {
      runInAction(() => {
        this.isGeocoding = false
      })
    }
  }

  // Вызывается при выборе подсказки из списка
  async selectSuggestion(suggestion) {
    this.searchText = suggestion.title.text
    this.suggestions = []
    this.isDropdownOpen = false

    // Отменяем любой текущий процесс обратного геокодирования,
    // так как пользователь выбрал конкретный адрес.
    if (this.geocodingController) {
      this.geocodingController.abort()
    }

    const geocodedResults = await this.search(suggestion.value)

    if (geocodedResults && geocodedResults.length > 0) {
      const firstResult = geocodedResults[0]
      const coords = [firstResult.geo.longitude, firstResult.geo.latitude]

      this.setPlacemark(coords)
      this.setLocation({ center: coords, zoom: 15 })
      this.selectedLocation = firstResult
    }
  }

  // Вызывается при клике на карту или в конце перетаскивания маркера
  async updateLocationFromCoords(eventData) {
    const coords = Array.isArray(eventData) ? eventData : eventData.coordinates

    if (!coords) return

    this.setPlacemark(coords)
    const result = await this.getAddressByCoords(coords)
    if (result) {
      this.selectedLocation = result
      this.searchText = result.text
    }
  }

  clearCache() {
    this.searchCache.clear()
    this.reverseGeocodeCache.clear()
    console.log('Кэш MapStore очищен.')
  }

  setCacheEnabled(enabled) {
    this.isCacheEnabled = !!enabled
    console.log(
      `Кэширование в MapStore ${this.isCacheEnabled ? 'включено' : 'отключено'}.`,
    )
  }
}

export default MapStore
